import cx from 'classnames'
import { Text } from '@cinch-labs/design-system'
import { currencyValueFormatter } from '@cinch-labs/shared-util'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import styles from './used-cars-finance-representative-example.module.css'

const formatCurrency = (value: number, includePence = true) =>
  currencyValueFormatter(value, false, { includePence: includePence })

interface UsedCarsFinanceFigures {
  cashPrice: number
  creditAmount: number
  customerDeposit: number
  representativeApr: number
  annualMileageLimit: number
  totalAmountPayable: number
  optionToPurchaseFee: number
  totalInterestAmount: number
  optionalFinalPayment: number
  monthlyPaymentsAmount: number
  fixedAnnualInterestRate: number
  numberOfMonthlyPayments: number
  excessMileageChargeInPence: number
}

export interface UsedCarsFinanceRepresentativeExampleProps
  extends UsedCarsFinanceFigures {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  minScreenWidthForTable?: number
}

export const generateUsedCarsRepresentativeExampleContent = ({
  cashPrice,
  creditAmount,
  customerDeposit,
  representativeApr,
  annualMileageLimit,
  totalAmountPayable,
  optionToPurchaseFee,
  totalInterestAmount,
  optionalFinalPayment,
  monthlyPaymentsAmount,
  fixedAnnualInterestRate,
  numberOfMonthlyPayments,
  excessMileageChargeInPence,
}: UsedCarsFinanceFigures) => {
  const formattedCashPrice = formatCurrency(cashPrice, false)
  const formattedCreditAmount = formatCurrency(creditAmount, false)
  const formattedCustomerDeposit = formatCurrency(customerDeposit, false)
  const formattedRepresentativeApr = Number(representativeApr)
  const formattedNumberOfMonthlyPayments = Number(numberOfMonthlyPayments)
  const formattedMonthlyPaymentsAmount = formatCurrency(monthlyPaymentsAmount)
  const formattedFixedAnnualInterestRate = Number(fixedAnnualInterestRate)
  const formattedOptionalFinalPayment = formatCurrency(optionalFinalPayment)
  const formattedOptionToPurchaseFee = formatCurrency(
    optionToPurchaseFee,
    false,
  )
  const formattedTotalInterestAmount = formatCurrency(totalInterestAmount)
  const formattedTotalAmountPayable = formatCurrency(totalAmountPayable)
  const formattedAnnualMileageLimit = annualMileageLimit.toLocaleString()
  const formattedExcessMileageChargeInPence = Number(excessMileageChargeInPence)

  return {
    title: 'Personal Contract Purchase (PCP) representative example',
    bodyCopy: `Based on a cash price of <strong>${formattedCashPrice}</strong> with a <strong>${formattedCustomerDeposit}</strong> customer deposit, borrowing ${formattedCreditAmount} at a representative APR of <strong>${formattedRepresentativeApr}%</strong> and the following:`,
    bodyCopyVariant: `Based on a cash price of ${formattedCashPrice}, with a ${formattedCustomerDeposit} customer deposit, borrowing ${formattedCreditAmount} at a representative APR of ${formattedRepresentativeApr}%. ${formattedNumberOfMonthlyPayments} monthly payments of ${formattedMonthlyPaymentsAmount}.  Fixed rate of interest per annum ${formattedFixedAnnualInterestRate}%.  Optional final payment ${formattedOptionalFinalPayment}.  Option-to-purchase fee ${formattedOptionToPurchaseFee}.  Amount of interest ${formattedTotalInterestAmount}.  Total amount payable ${formattedTotalAmountPayable}.  Annual mileage limit ${formattedAnnualMileageLimit} miles.  Excess mileage charge ${formattedExcessMileageChargeInPence}p per mile.`,
    descriptionList: [
      {
        term: `${formattedMonthlyPaymentsAmount} monthly payments of`,
        details: formattedMonthlyPaymentsAmount,
      },
      {
        term: 'Fixed rate of interest per annum',
        details: `${formattedFixedAnnualInterestRate}%`,
      },
      {
        term: 'Option-to-purchase fee',
        details: formattedOptionToPurchaseFee,
      },
      {
        term: 'Optional final payment',
        details: formattedOptionalFinalPayment,
      },
      {
        term: 'Amount of interest',
        details: formattedTotalInterestAmount,
      },
      {
        term: 'Total amount payable',
        details: formattedTotalAmountPayable,
      },
      {
        term: 'Annual mileage limit',
        details: `${formattedAnnualMileageLimit} miles`,
      },
      {
        term: 'Excess mileage charge',
        details: `${formattedExcessMileageChargeInPence}p per mile`,
      },
    ],
  }
}

export const UsedCarsFinanceRepresentativeExample = ({
  headingLevel = 'h3',
  minScreenWidthForTable = 0,
  ...finRepData
}: UsedCarsFinanceRepresentativeExampleProps) => {
  const { isDesktop } = useResponsiveEnvironment()

  const displayTable = isDesktop && window.innerWidth > minScreenWidthForTable
  const representativeExample =
    generateUsedCarsRepresentativeExampleContent(finRepData)

  if (displayTable) {
    return (
      <section
        className={styles.repExample}
        aria-labelledby="representativeExampleHeading"
        data-testid="representative-example"
      >
        <div className={styles.repHeader}>
          <Text
            id="representativeExampleHeading"
            element={headingLevel}
            fontWeight="bold"
            fontSize="s"
            className={styles.repTitle}
          >
            {representativeExample.title}
          </Text>
          <Text
            fontWeight="book"
            fontSizeFixed="xs"
            className={styles.repBody}
            dangerouslySetInnerHTML={{ __html: representativeExample.bodyCopy }}
          />
        </div>
        <dl className={styles.repDl} data-testid="rep-example-list">
          {representativeExample.descriptionList.map(
            (item: { term: string; details: string }) => (
              <div key={item.term} className={styles.repDlItem}>
                <Text
                  element="dt"
                  fontSizeFixed="xs"
                  fontWeight="book"
                  className={styles.repDt}
                >
                  {item.term}
                </Text>
                <Text element="dd" fontSizeFixed="xs" fontWeight="bold">
                  {item.details}
                </Text>
              </div>
            ),
          )}
        </dl>
      </section>
    )
  }

  return (
    <section
      className={cx(styles.repExample, styles.repExampleVariant)}
      aria-labelledby="representativeExampleHeading"
      data-testid="representative-example"
    >
      <div className={styles.repHeader}>
        <Text
          id="representativeExampleHeading"
          element={headingLevel}
          fontWeight="bold"
          fontSize="s"
          className={styles.repTitle}
        >
          {representativeExample.title}
        </Text>

        <Text
          fontWeight="book"
          fontSizeFixed="xs"
          className={styles.repBody}
          data-testid="body-copy-variant"
        >
          {representativeExample.bodyCopyVariant}
        </Text>
      </div>
    </section>
  )
}
